import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IonContent, ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { BehaviorSubject, first, firstValueFrom, Observable, skipWhile, switchMap, take } from 'rxjs';
import { AUTH_TOKEN } from '../../common/auth/auth-token.interceptor';
import { AuthService } from '../../common/auth/auth.service';
import { AbstractFetchAdaptor } from '../../common/fetch/abstract-fetch.adaptor';
import { AbstractRequestFormAdaptor } from '../../common/form/abstract-request-form.adaptor';
import { AssessmentFormResponse } from '../screening/screening-tool-form.adaptor';
import { SuccessModalComponent } from '../components/modals/modal-success';

export interface ProfileFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  contact_preference: string | null;
  appointment_preference: string | null;
  general_note: string | null;
  address: string;
  rvd_consent: string;
}

export enum UserLoginMethod {
  EMAIL_PASSWORD_LESS = 'email_password_less',
  PHONE_PASSWORD_LESS = 'phone_password_less',
  EMAIL_PASSWORD = 'email_password',
}

export enum ProfileUpdateContext {
  REGISTRATION = 'registration',
  PROFILE_EDIT = 'profile_edit',
}

export interface ProfileFetchResponse {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  login_method: UserLoginMethod;
  assessments: AssessmentFormResponse[];
  rvd_consent: string;
  contact_preference: string | null;
  appointment_preference: string | null;
  general_note: string | null;
  address: string;
  rvd_consent_updated_at: string;
}

@Injectable()
export class ProfileFetchAdaptor extends AbstractFetchAdaptor<object, ProfileFetchResponse> {
  override name = 'profile';

  dataSubject = new BehaviorSubject<ProfileFetchResponse | null>(null);
  data$ = this.dataSubject.pipe(skipWhile((data) => data == null)) as Observable<ProfileFetchResponse>;

  private auth = inject(AuthService);
  private http = inject(HttpClient);
  private router = inject(Router);

  refresh() {
    this.onRequest().subscribe({
      next: (res) => this.dataSubject.next(res),
      error: (err: unknown) => this.onError?.({}, err),
    });
  }

  override onRequest(): Observable<ProfileFetchResponse> {
    return this.auth.token$.pipe(
      first(),
      switchMap((token) =>
        this.http.get<ProfileFetchResponse>('/accounts/profile', {
          headers: new HttpHeaders().set('Accept', 'application/json'),
          context: new HttpContext().set(AUTH_TOKEN, token),
        }),
      ),
    );
  }

  override onSuccess(_props: object, res: ProfileFetchResponse) {
    this.dataSubject.next(res);

    const basicProfileComplete = res.contact_preference && res.rvd_consent != null;
    if (!basicProfileComplete) {
      this.router.navigate(['/basic-info'], { replaceUrl: true });
    }
  }
}

@Injectable()
export class ProfileUpdateAdaptor extends AbstractRequestFormAdaptor<ProfileFormData, object> {
  override name = 'profile';

  private context: ProfileUpdateContext = ProfileUpdateContext.PROFILE_EDIT;
  private ionContent: IonContent | null = null;

  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private profile = inject(ProfileFetchAdaptor);
  private modalCtrl = inject(ModalController);
  private router = inject(Router);
  private location = inject(Location);

  setContext(context: ProfileUpdateContext): void {
    this.context = context;
  }

  setIonContent(ionContent: IonContent): void {
    this.ionContent = ionContent;
  }

  override onRequest(formData: ProfileFormData) {
    // Exclude disabled fields (phone, email, date_of_birth) that survivors cannot edit
    // Only include fields that are actually editable from the profile page
    const editableData: Partial<ProfileFormData> = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      address: formData.address,
      contact_preference: formData.contact_preference,
      appointment_preference: formData.appointment_preference,
      general_note: formData.general_note,
      rvd_consent: formData.rvd_consent,
    };

    return this.auth.token$.pipe(
      first(),
      switchMap((token) =>
        this.http.put(
          '/accounts/profile',
          {
            account: editableData,
          },
          { context: new HttpContext().set(AUTH_TOKEN, token) },
        ),
      ),
    );
  }

  override async onSuccess(formData: ProfileFormData) {
    const currentProfile = await firstValueFrom(this.profile.data$.pipe(take(1)));

    const changesDetected = this.detectChanges(formData, currentProfile);

    if (changesDetected) {
      this.profile.dataSubject.next({ ...currentProfile, ...formData });
    }

    if (this.context === ProfileUpdateContext.REGISTRATION) {
      await this.handleRegistrationSuccess();
    } else {
      await this.handleProfileEditSuccess(changesDetected);
    }
  }

  private async handleRegistrationSuccess(): Promise<void> {
    // Just refresh profile, let ProfileFetchAdaptor handle navigation
    this.profile.refresh();
  }

  private async handleProfileEditSuccess(changesDetected: boolean): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: SuccessModalComponent,
      cssClass: 'custom-success-modal modal-box custom-modal',
      componentProps: {
        title: changesDetected ? 'Changes saved' : 'No changes to save',
        message: changesDetected
          ? 'Thank you. Your changes have been saved to your profile.'
          : 'No changes were made to your profile.',
        buttons: [
          {
            text: 'Go to my dashboard',
            fill: 'clear',
            cssClass: 'tertiaryBtn',
            handler: (): boolean => {
              modal.dismiss(null, 'dashboard');
              return false;
            },
          },
          {
            text: 'Show my profile again',
            fill: 'clear',
            cssClass: 'primaryBtn',
            handler: (): boolean => {
              modal.dismiss(null, 'profile');
              return false;
            },
          },
        ],
      },
    });

    await modal.present();

    const { role } = await modal.onWillDismiss();

    if (role === 'dashboard') {
      await this.router.navigate(['/dashboard'], {
        replaceUrl: true,
      });
    } else if (role === 'profile') {
      await this.navigateToProfile();
    }
  }

  private async navigateToProfile(): Promise<void> {
    const currentUrl = this.router.url.split('#')[0].split('?')[0];

    if (currentUrl === '/profile') {
      // Same route - refresh data and scroll to top
      this.profile.refresh();
      await this.scrollToTop();
    } else {
      // Different route - normal navigation
      await this.router.navigate(['/profile'], {});
    }
  }

  private updateUrlWithFragment(fragment: string): void {
    const urlTree = this.router.createUrlTree(['/profile'], {
      fragment: fragment,
      queryParamsHandling: 'preserve',
    });
    this.location.go(urlTree.toString());
  }

  private async scrollToTop(): Promise<void> {
    // Try to use IonContent's scrollToTop if available
    if (this.ionContent) {
      await this.ionContent.scrollToTop(300); // 300ms animation duration
    } else {
      // Fallback to window.scrollTo
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  private async scrollToFragment(fragment: string): Promise<void> {
    // Wait for DOM to be ready
    await new Promise((resolve) => setTimeout(resolve, 100));

    const element = document.getElementById(fragment);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      // Focus for accessibility
      setTimeout(() => {
        element.setAttribute('tabindex', '-1');
        element.focus();
        setTimeout(() => element.removeAttribute('tabindex'), 100);
      }, 500);
    }
  }

  private detectChanges(formData: ProfileFormData, profile: ProfileFetchResponse): boolean {
    return Object.keys(formData).some((key) => {
      if (key in profile) {
        const profileKey = key as keyof typeof profile;
        const formKey = key as keyof typeof formData;
        return profile[profileKey] !== formData[formKey];
      }
      // If key doesn't exist in profile but exists in formData, that's a change
      return true;
    });
  }
}
